import * as React from 'react';
import {
	clearTabActivityIndicator,
	isTabActivityIndicatorShown,
	showTabActivityIndicator,
} from '../utils/tabActivityIndicator';
import { StreamingStatus } from './useStreamingResponseMutation';

const isActiveStatus = (status: StreamingStatus) => status === 'loading' || status === 'streaming';

/** Tab state is a page-level singleton — only one component tracking a stream should mount this hook at a time. */
export function useTabActivityIndicator(status: StreamingStatus) {
	const previousStatusRef = React.useRef<StreamingStatus>('idle');

	React.useEffect(() => {
		const wasActive = isActiveStatus(previousStatusRef.current);
		previousStatusRef.current = status;

		if (isActiveStatus(status)) {
			// Module state (not a ref) decides visibility: refs survive a StrictMode unmount/remount
			// whose cleanup cleared the indicator, and the remount must re-show it.
			if (!wasActive || !isTabActivityIndicatorShown()) {
				showTabActivityIndicator('generating');
			}
			return;
		}

		if (!isTabActivityIndicatorShown()) {
			return;
		}

		// A non-active -> non-active transition (e.g. error -> idle via reset) while an indicator is
		// still displayed would otherwise leave it stranded with no listeners to clear it.
		if (!wasActive || !document.hidden) {
			clearTabActivityIndicator();
			return;
		}

		showTabActivityIndicator(status === 'error' ? 'error' : 'complete');
		const handleReturnToTab = () => {
			if (!document.hidden) {
				clearTabActivityIndicator();
			}
		};
		document.addEventListener('visibilitychange', handleReturnToTab);
		window.addEventListener('focus', handleReturnToTab);
		return () => {
			document.removeEventListener('visibilitychange', handleReturnToTab);
			window.removeEventListener('focus', handleReturnToTab);
		};
	}, [status]);

	React.useEffect(() => {
		return () => clearTabActivityIndicator();
	}, []);
}
